import { type SyncPositionInfo } from "~/graphql/supergraphTypes";
import { DATE_FORMATS } from "~/utils/constants";
import { toStandardFormat } from "~/utils/datetime";
import { getCapitalActivityCopy, TransactionCategory } from "../../../types";
import { SYNC_SETTINGS_TYPES } from "./constants";
import { InitialValues } from "./types";

const getCategoryCopy = (
  showCapitalActivityLabelCanopy: boolean,
): Record<TransactionCategory, { singular: string; plural: string }> => ({
  [TransactionCategory.CapitalCalls]: { singular: "capital call", plural: "capital calls" },
  [TransactionCategory.Redemptions]: getCapitalActivityCopy(showCapitalActivityLabelCanopy),
  [TransactionCategory.Distributions]: { singular: "distribution", plural: "distributions" },
  [TransactionCategory.NetIncome]: { singular: "net income", plural: "net income" },
});

const joinWithAnd = (items: string[]): string => {
  if (items.length <= 1) {
    return items[0] ?? "";
  }
  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

export const getUnmappedCategoriesTitle = (
  categories: TransactionCategory[],
  showCapitalActivityLabelCanopy = false,
): string => {
  const copy = getCategoryCopy(showCapitalActivityLabelCanopy);
  const joined = joinWithAnd(categories.map((category) => copy[category].plural));
  const sentence = joined.charAt(0).toUpperCase() + joined.slice(1);
  const verb =
    categories.length === 1 && categories[0] === TransactionCategory.NetIncome ? "is" : "are";
  return `${sentence} ${verb} not included in the mapping`;
};

export const getUnmappedCategoriesSingularList = (
  categories: TransactionCategory[],
  showCapitalActivityLabelCanopy = false,
): string => {
  const copy = getCategoryCopy(showCapitalActivityLabelCanopy);
  return joinWithAnd(categories.map((category) => copy[category].singular));
};

export const getUnmappedCategoriesPluralList = (
  categories: TransactionCategory[],
  showCapitalActivityLabelCanopy = false,
): string => {
  const copy = getCategoryCopy(showCapitalActivityLabelCanopy);
  return joinWithAnd(categories.map((category) => copy[category].plural));
};

export const combinePositionLists = (lists: SyncPositionInfo[][]) => {
  // Combine all position lists into a single list, where id is deduped
  const combinedList: SyncPositionInfo[] = [];
  const idSet = new Set<number>();
  lists.forEach((list) => {
    list.forEach((position) => {
      if (!idSet.has(position.id)) {
        combinedList.push(position);
        idSet.add(position.id);
      }
    });
  });

  return combinedList;
};

export const combineUniqueStrings = (lists: (string[] | null | undefined)[]) => {
  const combinedList: string[] = [];
  const valueSet = new Set<string>();
  lists.forEach((list) => {
    (list ?? []).forEach((value) => {
      if (!valueSet.has(value)) {
        combinedList.push(value);
        valueSet.add(value);
      }
    });
  });

  return combinedList.sort();
};

export const getEndDate = (values: InitialValues): string | null => {
  if (values.syncSettings === SYNC_SETTINGS_TYPES.empty && values.firstIntegrationThroughDate) {
    return toStandardFormat(
      values.firstIntegrationThroughDate,
      DATE_FORMATS["YYYY-MM-DD"].long,
    );
  }
  if (
    values.syncSettings === SYNC_SETTINGS_TYPES.allTransactions &&
    values.integrationThrough
  ) {
    return toStandardFormat(
      new Date(values.integrationThrough),
      DATE_FORMATS["YYYY-MM-DD"].long,
    );
  }
  if (
    values.syncSettings === SYNC_SETTINGS_TYPES.sinceLastSync &&
    values.updateDateRange === "custom" &&
    values.customDate
  ) {
    return toStandardFormat(new Date(values.customDate), DATE_FORMATS["YYYY-MM-DD"].long);
  }
  if (
    values.syncSettings === SYNC_SETTINGS_TYPES.sinceLastSync &&
    values.updateDateRange !== "custom"
  ) {
    return toStandardFormat(new Date(values.updateDateRange), DATE_FORMATS["YYYY-MM-DD"].long);
  }
  return null;
};

export const handleValidate = (values: InitialValues) => {
  const integrationStartErrorMessage = "Select a integration start date";
  const integrationThroughErrorMessage = "Select a integration through date";
  if (
    values?.syncSettings === SYNC_SETTINGS_TYPES.allTransactions &&
    !values.useInceptionDate &&
    values.integrationStart == null &&
    values?.integrationThrough == null
  ) {
    return {
      integrationStart: integrationStartErrorMessage,
      integrationThrough: integrationThroughErrorMessage,
    };
  }
  if (
    values?.syncSettings === SYNC_SETTINGS_TYPES.allTransactions &&
    !values.useInceptionDate &&
    values.integrationStart == null
  ) {
    return {
      integrationStart: integrationStartErrorMessage,
    };
  }
  if (
    values?.syncSettings === SYNC_SETTINGS_TYPES.allTransactions &&
    values?.integrationThrough == null
  ) {
    return {
      integrationThrough: integrationThroughErrorMessage,
    };
  }

  if (values?.syncSettings === SYNC_SETTINGS_TYPES.sinceLastSync) {
    if (values?.updateDateRange === "custom" && values.customDate == null) {
      return {
        customDate: "Select custom GL date",
      };
    }
  }

  if (
    values?.syncSettings == SYNC_SETTINGS_TYPES.empty &&
    values?.updateDateRange === "" &&
    values?.firstIntegrationThroughDate === null
  ) {
    return {
      firstIntegrationThroughDate: "Select custom GL date",
    };
  }

  return undefined;
};

export const normalizeSharedGlStartDate = (
  values: {
    capitalCalls: boolean;
    capitalActivity: boolean;
    distributions: boolean;
    netIncome: boolean;
  },
  glStartDateCapitalCall?: string | null,
  glStartDateCapitalTransaction?: string | null,
  glStartDateDistribution?: string | null,
  glStartDateNetIncome?: string | null,
): string | null => {
  const INCEPTION_DATE = "Inception";
  const normalizeDate = (date: string | null | undefined): string => {
    return date ? toStandardFormat(date, DATE_FORMATS["YYYY-MM-DD"].long) : INCEPTION_DATE;
  };

  const selectedDates: string[] = [];
  if (values.capitalCalls) {
    selectedDates.push(normalizeDate(glStartDateCapitalCall));
  }
  if (values.capitalActivity) {
    selectedDates.push(normalizeDate(glStartDateCapitalTransaction));
  }
  if (values.distributions) {
    selectedDates.push(normalizeDate(glStartDateDistribution));
  }
  if (values.netIncome) {
    selectedDates.push(normalizeDate(glStartDateNetIncome));
  }

  if (selectedDates.length === 0) {
    return null;
  }
  const first = selectedDates[0];
  const allSame = selectedDates.every((d) => d === first);
  return allSame ? first : INCEPTION_DATE;
};
